import { useEffect, useLayoutEffect } from 'react'

import { UseFormGetValues, UseFormReset, UseFormSetValue } from 'react-hook-form'

import authCookieManager from '@App/cookies/auth_cookie_manager'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import { Wallet } from '@App/interfaces/CommonInterfaces'
import { makeSpotOrderRequest } from '@App/redux/slices/sockets/FrontServiceConnectionSlice'
import { clearEditOrder } from '@App/redux/slices/trade/trades/OrdersSlice'
import { Order, StopLoss, TakeProfit } from '@App/redux/slices/trade/trades/types'

import { currentUser } from '@Redux/slices/user/selectors'

import { FormType, SportLossValues, TakeProfitValues } from '../../model'

export const useEditOrder = ({
  order,
  wallet,
  orderType,
  getValues,
  setValue,
  reset,
  getStopLossValues,
  getTakeProfitValues,
}: {
  order: Order
  wallet: Wallet | null
  orderType: string
  getValues: UseFormGetValues<FormType>
  setValue: UseFormSetValue<FormType>
  reset: UseFormReset<FormType>
  getStopLossValues: (rate: number, quantity: number, stopLoss?: StopLoss) => SportLossValues
  getTakeProfitValues: (rate: number, quantity: number, takeProfit?: TakeProfit) => TakeProfitValues
}) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(currentUser)

  const onSubmit = (data: FormType) => {
    if (wallet) {
      dispatch(
        makeSpotOrderRequest({
          ...data,
          take_profit_trigger_type: data.take_profit_trigger_type ?? undefined,
          stop_loss_trigger_type: data.stop_loss_trigger_type,
          cancellation_date: data.cancellation_date?.toISOString() ?? null,
          order_type: orderType as any,
          token: authCookieManager.getAccessToken() || '',
          user_id: user.user_id,
          amount: data.amount,
          market_id: order.market.id,
          action: order.action_type,
          rate: data.price,
          wallet_id: wallet.id as any as number,
          type: 'change',
          trade_id: order.id,
        }),
      )
    }
    dispatch(clearEditOrder())
  }

  const resetForm = () => {
    const values = getValues()
    reset({
      ...values,
      wallet_value: wallet?.amount ?? 0,
      amount: order.quantity,
      price_in_wallet_currency: order.amount,
      trigger_price_active: true,
      trigger_price: order.trading_bet_order.limit_price,
      cancellation_date: order.trading_bet_order?.cancellation_time ? new Date(order.trading_bet_order.cancellation_time) : null,
      ...getStopLossValues(order.open_rate, order.quantity, order?.stop_loss),
      ...getTakeProfitValues(order.open_rate, order.quantity, order?.take_profit)
    })
  }

  useLayoutEffect(() => {
    resetForm()
  }, [order])

  useEffect(() => {
    if (!wallet) return

    setValue('wallet_value', wallet.amount, { shouldDirty: false })
  }, [wallet?.amount])

  return { onSubmit }
}
